import React, { useState, useEffect, useRef, useCallback } from 'react'
import { normalizeAmount } from '@@partner:helpers/cart'

import { Input, IconButton } from 'rsuite'
import LegacyCloseIcon from '@rsuite/icons/legacy/Close'
import LegacyMinusIcon from '@rsuite/icons/legacy/Minus'
import LegacyCheckIcon from '@rsuite/icons/legacy/Check'
import LegacyPlusIcon from '@rsuite/icons/legacy/Plus'

import './ProductCardControls.scss'

const normalizeInputAmount = (amount: number, max = 0) => {
  const normalized = normalizeAmount(amount)

  if (max > 0) {
    return Math.max(0, Math.min(max, normalized))
  }

  return Math.max(0, normalized)
}

export interface ProductCardControlsProps {
  amount: number
  amountStep: number
  maxAmount?: number
  onAmountChange?: (amount: number) => void
}

function ProductCardControls ({
  amount,
  amountStep,
  maxAmount = 0,
  onAmountChange
}: ProductCardControlsProps) {
  const [ isEditing, setIsEditing ] = useState(false)
  const cartAmountInputRef = useRef<HTMLInputElement>(null)
  const [ uiAmount, setUiAmount ] = useState(0)
  const [ uiAmountBeforeEditing, setUiAmountBeforeEditing ] = useState(0)

  useEffect(() => {
    setUiAmount(amount)
  }, [ amount ])

  const manuallyBlurInput = useCallback(() => {
    if (cartAmountInputRef?.current?.blur) {
      cartAmountInputRef.current.blur()
    }
  }, [ cartAmountInputRef ])

  const updateCartAmount = (newAmount: any) => {
    const amount = normalizeInputAmount(newAmount, maxAmount)

    if (amount >= 0) {
      setUiAmount(amount)
      onAmountChange?.(amount)
    }
  }

  const handleAmountChange = (newAmount: any) => {
    const amount = normalizeInputAmount(newAmount, maxAmount)

    if (!isEditing) {
      updateCartAmount(amount)
    }

    setUiAmount(amount)
  }

  const handleDecreaseAmount = () => {
    handleAmountChange(uiAmount - amountStep)
  }

  const handleIncreaseAmount = () => {
    handleAmountChange(uiAmount + amountStep)
  }

  const handleFocusInput = (event: any) => {
    setIsEditing(true)
    setUiAmountBeforeEditing(uiAmount)
    event.target.select()
  }

  const handleBlurInput = () => {
    setTimeout(() => {
      setIsEditing(false)
      updateCartAmount(uiAmount)
    }, 400)
  }

  const handleApplyInputChange = (amount: any) => (_event: any) => {
    manuallyBlurInput()
    updateCartAmount(amount)
    setIsEditing(false)
  }

  return (
    <div className="ProductCardControls">
      <div
        className={`ProductCardControls__Cont ${
          isEditing && 'ProductCardControls__Cont--isEditing'
        }`}
      >
        {isEditing ? (
          <IconButton
            icon={<LegacyCloseIcon />}
            className="ProductCardControls__Cont__Cancel"
            onClick={handleApplyInputChange(uiAmountBeforeEditing)}
            appearance="primary"
          />
        ) : (
          <IconButton
            icon={<LegacyMinusIcon />}
            className="ProductCardControls__Cont__Minus"
            onClick={handleDecreaseAmount}
            appearance="primary"
          />
        )}
        <Input
          className="ProductCardControls__Cont__Input"
          pattern="\d*"
          inputMode="numeric"
          inputRef={cartAmountInputRef}
          value={`${uiAmount}`}
          min={0}
          step={amountStep}
          onChange={handleAmountChange}
          onBlur={handleBlurInput}
          onFocus={handleFocusInput}
          onPressEnter={handleApplyInputChange(uiAmount)}
        />
        {isEditing ? (
          <IconButton
            icon={<LegacyCheckIcon />}
            className="ProductCardControls__Cont__Apply"
            onClick={handleApplyInputChange(uiAmount)}
            color="green"
            appearance="primary"
          />
        ) : (
          <IconButton
            icon={<LegacyPlusIcon />}
            className="ProductCardControls__Cont__Plus"
            onClick={handleIncreaseAmount}
            color="green"
            appearance="primary"
          />
        )}
      </div>
    </div>
  )
}

export default React.memo(ProductCardControls)
